import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect } from 'react'

import { axios, queryClient } from '@apps-common/lib'
import { IManagementUnitSearchModel, IManagementUnitSearchParamsModel } from '@apps-common/types/management/unit'

export const QUERY_KEY_MANAGEMENT_SEARCH_UNITS_INFINITE = (model?: IManagementUnitSearchParamsModel, hideOrderUnits?: boolean) =>
  [
    'searchManagementUnits',
    model?.status,
    model?.mCentreId,
    model?.companyId,
    model?.techCompanyId,
    model?.site,
    model?.accountNo,
    model?.imei,
    model?.serialNo,
    model?.mobileNo,
    model?.ipAddress,
    model?.simNo,
    model?.unprocessedCancellations,
    model?.pinnedUnits,
    model?.startDate,
    model?.endDate,
    // model?.skip,
    // model?.take,
    model?.orderBy,
    hideOrderUnits
  ].filter(Boolean)

export const searchUnitsInfinite = async (
  model: IManagementUnitSearchParamsModel,
  skip: number,
  take: number,
  hideOrderUnits = false
): Promise<IManagementUnitSearchModel[]> =>
  axios.post(`/management/unit/Units?hideOrderUnits=${hideOrderUnits}`, { ...model, skip, take }).then((res) => res.data?.data)

type Props = {
  enabled: boolean
  model: IManagementUnitSearchParamsModel
  hideOrderUnits?: boolean
}

export const useManagementSearchUnitsInfinite = ({ enabled = true, model, hideOrderUnits = false }: Props) => {
  const { isLoading, isFetching, isRefetching, isError, data, refetch, fetchNextPage, hasNextPage } = useInfiniteQuery({
    enabled,
    queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNITS_INFINITE(model, hideOrderUnits),
    initialPageParam: 0,
    queryFn: ({ pageParam = 0 }) => searchUnitsInfinite({ ...model }, pageParam, model?.take, hideOrderUnits),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage?.length < model.take) {
        return null
      }

      return allPages?.reduce((acc, curr) => acc + (curr?.length || 0), 0)
    }
  })

  const refetchOverride = async () => {
    await queryClient.cancelQueries({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNITS_INFINITE(model) })
    queryClient.removeQueries({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNITS_INFINITE(model) })
    await queryClient.invalidateQueries({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNITS_INFINITE(model) })
    refetch()
  }

  useEffect(
    () => () => {
      queryClient.removeQueries({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNITS_INFINITE() })
    },
    []
  )

  return {
    isLoading,
    isFetching,
    isRefetching,
    isError,
    data,
    refetch: refetchOverride,
    fetchNextPage,
    hasNextPage
  }
}
